import { useState } from 'react'
import { Lock } from '@phosphor-icons/react'
import { useActor } from '@/auth/pin/phoneSession'
import { useFormat, useI18n } from '@/i18n'
import { newId } from '@/lib/ids'
import { enqueueCommand } from '@/offline'
import type { ScanMeta } from '@/ui'
import { Banner, BilingualLabel, Button, Card, CardBody, CardHeader, EmptyState, useToast } from '@/ui'
import type { WeighInPayload } from '../../commands'
import '../../commands'
import { runParentIds, type ProcessState, type RunView } from '../../projection'
import { checkWeighInBag, issuedNotIn, needsSecondEntry, runFigures, type WeighInMatch } from '../../model/rules'
import { Counter, Line, ReadingField, ScanButton, type Reading } from '../../components/bits'
import { useRefusal } from '../../components/useText'

interface Pending {
  match: WeighInMatch
  qr: string
  entry: ScanMeta['entry']
}

/**
 * F6.3 — Weigh in: scan each bag at the hopper. Its weight is the issue weight; only a bag whose
 * issue this phone has not seen is weighed here. The scan uses the tag up on this phone at once.
 */
export function WeighInSection({
  state,
  run,
  onGoCleanout,
}: {
  state: ProcessState
  run: RunView
  onGoCleanout: () => void
}) {
  const { t } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const actor = useActor()
  const refusal = useRefusal()
  const [pending, setPending] = useState<Pending | null>(null)
  const [reading, setReading] = useState<Reading | null>(null)
  const [saving, setSaving] = useState(false)
  const fig = runFigures(run, state.steps.get(run.step_code))
  const notIn = issuedNotIn(run)
  const issuedCount = run.issued.size

  if (!run.cleanout_confirmed_at)
    return (
      <EmptyState
        icon={<Lock />}
        title={<BilingualLabel k="process.weighIn.locked" layout="stacked" />}
        description={t('process.weighIn.lockedBody')}
        action={
          <Button size="touch" onClick={onGoCleanout}>
            <BilingualLabel k="process.tab.cleanout" />
          </Button>
        }
      />
    )

  const bagIn = async () => {
    if (!pending) return
    const { match } = pending
    if (!match.issueSeen && !reading) return
    setSaving(true)
    try {
      const id = newId()
      const payload: WeighInPayload = {
        run_input_id: id,
        run_id: run.id,
        qr_payload: pending.qr,
        entry: pending.entry,
        container_id: match.bag.container_id,
        weight_read_g: match.issueSeen ? null : reading!.read_g,
        issue_seen: match.issueSeen,
        confirmed_twice: match.issueSeen ? false : reading!.confirmed_twice,
      }
      await enqueueCommand({
        id,
        type: 'weigh_in',
        payload,
        actorId: actor.user_id,
        parentIds: runParentIds(run),
      })
      toast.success(t('process.weighIn.saved', { tag: pending.qr }))
      setPending(null)
      setReading(null)
    } catch (e) {
      toast.error(t('process.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  const pUnit = pending ? state.units.get(pending.match.bag.unit_id) : undefined

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2">
        <Counter
          label={t('process.weighIn.bagsIn')}
          value={run.inputs.length}
          total={issuedCount || undefined}
          tone={issuedCount > 0 && notIn.length === 0 ? 'ok' : 'neutral'}
        />
        <Counter label={t('process.weighIn.kgIn')} value={Math.round(fig.input_g / 1000)} />
      </div>

      {!pending && (
        <ScanButton
          continuous={false}
          label={<BilingualLabel k="process.weighIn.scan" />}
          validate={(c) => {
            const v = checkWeighInBag(state, run, c)
            return v.ok ? null : refusal(v.reason)
          }}
          onScan={(c, meta) => {
            const v = checkWeighInBag(state, run, c)
            if (v.ok) {
              setPending({ match: v.value, qr: c.trim().toUpperCase(), entry: meta.entry })
              setReading(null)
            }
          }}
        />
      )}

      {pending && (
        <Card className="border-ink">
          <CardHeader eyebrow={<BilingualLabel k="process.weighIn.bag" />} title={<span className="font-mono">{pending.qr}</span>} />
          <CardBody className="flex flex-col gap-3 py-2">
            <div>
              <Line label={t('process.lot')} value={pUnit?.code ?? '—'} />
              <Line label={t('process.farmer')} value={pUnit?.farmer_code ?? '—'} />
              {pending.match.issueSeen && (
                <Line
                  label={<BilingualLabel k="process.weighIn.issueWeight" />}
                  value={fmt.weight(pending.match.bag.issue_net_g ?? pending.match.bag.net_g_current ?? 0)}
                  strong
                />
              )}
            </div>
            {!pending.match.issueSeen && (
              <>
                <Banner tone="warn">{t('process.weighIn.notSeen')}</Banner>
                <ReadingField
                  label={<BilingualLabel k="process.weighIn.weight" />}
                  hint={t('process.weighIn.weightHint')}
                  needsTwice={(g) => needsSecondEntry(g, state.pack.settings)}
                  onChange={setReading}
                />
              </>
            )}
            <Button
              size="xl"
              fullWidth
              loading={saving}
              disabled={!pending.match.issueSeen && !reading}
              onClick={() => void bagIn()}
            >
              <BilingualLabel k="process.weighIn.bagIn" />
            </Button>
            <p className="text-[13px] text-muted">{t('process.weighIn.tinHint')}</p>
            <Button variant="ghost" size="touch" onClick={() => setPending(null)}>
              {t('common.cancel')}
            </Button>
          </CardBody>
        </Card>
      )}

      {notIn.length > 0 && (
        <Card>
          <CardHeader eyebrow={t('process.weighIn.waiting', { n: notIn.length })} />
          <CardBody className="py-1">
            <ul className="flex flex-wrap gap-2 py-2">
              {notIn.map((cid) => (
                <li key={cid} className="rounded-chip border border-rule px-2 py-1 font-mono text-[13px]">
                  {state.bags.get(cid)?.qr_payload ?? cid}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {run.inputs.length > 0 && (
        <Card>
          <CardHeader eyebrow={t('process.weighIn.inList')} />
          <CardBody className="py-1">
            <ul>
              {[...run.inputs].reverse().map((i) => (
                <li key={i.id} className="flex items-baseline justify-between border-b border-rule/70 py-2 last:border-b-0">
                  <span className="font-mono text-[14px]">{i.qr_payload}</span>
                  <span className="flex items-baseline gap-2 text-[13px] text-muted">
                    {i.context === 'WEIGH_IN' && <span>{t('process.weighIn.weighedHere')}</span>}
                    <span className="font-mono tabular text-ink">{i.weight_g !== null ? fmt.weight(i.weight_g) : '—'}</span>
                  </span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
    </div>
  )
}
